import React, { useState, useEffect } from "react";
import { PaginatedOrderResponse, Order } from "../../Interfaces/Orders";
import OrderDetailsModal from "../../components/Warehouse/OrderDetailsModal";
import "./AllOrderDisplay.css";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import fetchWithAuth from "../../services/fetchWithAuth";
import PrintComponent from "../../components/PrintableComponent";
import { renderOrderPrintoutToString } from "../../components/Warehouse/OrderPrintout";
import { useCart } from "../../contexts/Cart/CartContext";

const BASE_API = config.API_BASE_URL;

const WarehouseOrderDisplay: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const {
    baseShippingCost,
    freeShippingThreshold,
    freeShippingExceptions,
    baseShippingOverride,
  } = useCart();

  // Fetch itemsPerPage from the database when the component mounts
  useEffect(() => {
    const fetchItemsPerPage = async () => {
      try {
        const response = await fetch(
          `${BASE_API}config-options/order_pagination_items_per_page`
        );
        const data = await response.json();
        setItemsPerPage(data[0].config_value || 5);
      } catch (error) {
        console.error("Error fetching itemsPerPage:", error);
      }
    };

    fetchItemsPerPage();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetchWithAuth(
          `${BASE_API}orders/paginated?page=${page}&itemsPerPage=${itemsPerPage}&search=${searchQuery}`
        );
        const data: PaginatedOrderResponse = await response.json();

        setOrders(data.results);
        setTotalPages(Math.ceil(data.count / itemsPerPage));
      } catch (error) {
        console.error("Error fetching orders:", error);
      }
    };

    // Only fetch orders if itemsPerPage is available
    if (itemsPerPage) {
      fetchOrders();
    }
  }, [page, searchQuery, itemsPerPage]); // Re-run when page, searchQuery, or itemsPerPage changes

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1); // Reset to the first page on search
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleGoBackButton = () => {
    navigate("/warehouse/orders");
  };

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  return (
    <div className="warehouse-order-display">
      <button onClick={handleGoBackButton}>Go Back</button>
      <h1 className="warehouse-order-title">All Orders</h1>
      <input
        type="text"
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search by order number or ship-to name"
        className="warehouse-order-search"
      />
      <table className="warehouse-order-table">
        <thead>
          <tr>
            <th>Order Number</th>
            <th>Ship To</th>
            <th>Order Date</th>
            <th>Order Status</th>
            <th>Total Items</th>
            <th>Print</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.order_id}>
              <td>
                <span
                  className="warehouse-order-number clickable"
                  onClick={() => handleViewDetails(order)}
                >
                  {order.order_id}
                </span>
              </td>
              <td>{order.shipping_info?.ship_to_name || "No Data"}</td>
              <td>{order.created_at.split("T")[0]}</td>
              <td>{order.latest_status.status}</td>
              <td>{order.item_count}</td>
              <td>
                <PrintComponent
                  content={renderOrderPrintoutToString(
                    order,
                    baseShippingCost,
                    freeShippingThreshold,
                    freeShippingExceptions,
                    baseShippingOverride
                  )}
                  styles=""
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="warehouse-order-pagination">
        <button
          className="pagination-button"
          disabled={page === 1}
          onClick={handlePreviousPage}
        >
          Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          className="pagination-button"
          disabled={page === totalPages}
          onClick={handleNextPage}
        >
          Next
        </button>
      </div>

      {selectedOrder && (
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          order={selectedOrder}
        />
      )}
    </div>
  );
};

export default WarehouseOrderDisplay;
